import { useEffect, useRef } from 'react';

const sameSelection = (left, right) => left?.id === right?.id
  && left?.quoteVersionId === right?.quoteVersionId;

// Editor objects are immutable. Check again in the reducer's functional update:
// a user action may already be queued when the network promise resolves.
export default function useQuoteEditorOperation({ editor, setEditor, setSaving, setError, setStatusMessage }) {
  const latest = useRef(editor);
  latest.current = editor;
  const active = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; active.current = null; };
  }, []);
  useEffect(() => {
    if (active.current && !sameSelection(latest.current, active.current.editor)) {
      active.current = null;
      setSaving(false);
    }
  }, [editor?.id, editor?.quoteVersionId, setSaving]);

  const selected = (operation) => mounted.current && active.current === operation
    && sameSelection(latest.current, operation.editor);
  return {
    begin(label) {
      if (!mounted.current || active.current || !editor || latest.current !== editor) return null;
      if (editor.serverCalculationStatus === 'stale' || editor.serverWriteConflict) {
        setError(editor.serverWriteConflict
          ? 'Une commande a réussi sur le serveur. Conservez vos saisies puis rechargez explicitement la version avant une nouvelle écriture.'
          : 'Enregistrez vos modifications avant cette commande, ou renoncez à la commande pour conserver votre travail.');
        return null;
      }
      const operation = { editor, label, succeeded: false };
      active.current = operation;
      setSaving(true);
      setError(null);
      setStatusMessage(null);
      return operation;
    },
    selected,
    succeeded(operation) { operation.succeeded = true; },
    apply(operation, next, { allowVersionChange = false, minimumRevision } = {}) {
      if (!selected(operation)) return false;
      const conflict = { quoteId: next.id, quoteVersionId: next.quoteVersionId, command: operation.label };
      const changed = latest.current !== operation.editor;
      const stale = (!allowVersionChange && !sameSelection(operation.editor, next))
        || (sameSelection(operation.editor, next)
          && (Number(next.revision) < Number(operation.editor.revision)
            || (minimumRevision != null && Number(next.revision) < Number(minimumRevision))));
      setEditor((current) => {
        if (!sameSelection(current, operation.editor)) return current;
        if (current !== operation.editor || stale) return { ...current, serverWriteConflict: conflict };
        if (sameSelection(current, next) && Number(next.revision) < Number(current.revision)) {
          return { ...current, serverWriteConflict: conflict };
        }
        return next;
      });
      if (changed || stale) {
        setStatusMessage(null);
        setError('La commande a réussi sur le serveur. Vos nouvelles saisies ont été conservées. Rechargez explicitement la version avant une nouvelle écriture.');
      }
      return !changed && !stale;
    },
    fail(operation, error, reportRevisionError) {
      if (!selected(operation)) return;
      if (operation.succeeded) {
        setEditor((current) => sameSelection(current, operation.editor)
          ? { ...current, serverWriteConflict: { quoteId: current.id, quoteVersionId: current.quoteVersionId, command: operation.label } }
          : current);
        setStatusMessage(null);
        setError(`La commande a réussi sur le serveur, mais le rechargement a échoué. Vos données locales sont conservées. Rechargez explicitement la version. ${error.message || ''}`);
      } else if (reportRevisionError) reportRevisionError(error);
      else setError(error.message || 'La commande a échoué. Vos données locales sont conservées.');
    },
    finish(operation) {
      if (active.current !== operation) return;
      active.current = null;
      if (mounted.current) setSaving(false);
    },
  };
}
